import difference from "ramda/es/difference";

import {
  Scope,
  SolutionMap,
  SolutionOptionKey,
} from "@labs/shared/types/Solution.ts";
import { Recommendation } from "@labs/shared/types/Recommendation.ts";
import { getSolutionValueDecimal } from "./getSolutionValueDecimal";
import { EmissionsBySource } from "@labs/shared/types/EmissionsBySource.ts";
import { getSolutionValuePercent } from "./getSolutionValuePercent";
import { recommendations } from "./recommendations";
import { RecommendationId } from "@labs/shared/types/RecommendationId.ts";
import { Target } from "@labs/shared/types/Target.ts";
import { solutionOptions } from "../../data/solutionOptions.tsx";
import { FuelSource } from "@labs/shared/types/FuelSource.ts";
import { CarbonImpactByScope } from "@labs/shared/types/AnalyzedPlan.ts";

const ELECTRIC_EFFICIENCY_TARGET = 25;
const NATURAL_GAS_EFFICIENCY_TARGET = 15;
const ELECTRIC_EFFICIENCY_LOW_THRESHOLD = 20;
const ELECTRIC_EFFICIENCY_HIGH_THRESHOLD = 30;
const NATURAL_GAS_EFFICIENCY_LOW_THRESHOLD = 10;
const NATURAL_GAS_EFFICIENCY_HIGH_THRESHOLD = 20;
const ELECTRIFICATION_HIGH_THRESHOLD = 50;
const NATURAL_GAS_EMISSIONS_THRESHOLD = 20;
const TARGET_MIN = 40;
const EMISSION_CONTRIBUTION_THRESHOLD = 5;
const OFFSET_THRESHOLD = 0;
const CARBON_CAPTURE_THRESHOLD = 5;
const REC_THRESHOLD = 20;
const PPA_THRESHOLD = 30;
const SOLAR_HIGH_THRESHOLD = 30;
const GAP_THRESHOLD = 5;
const SCOPE_PROPORTION_THRESHOLD = 0.2;

/**
 * Calculates the priority based on the deviation from the target value.
 * @param actualValue The actual value.
 * @param targetValue The target value.
 * @returns The priority as a number.
 */
const calculatePriority = (actualValue: number, targetValue: number): number =>
  Math.abs(targetValue - actualValue);

/**
 * Generates recommendations based on the solution map and baseline emissions.
 */
export const generateRecommendations = (
  solutionMap: SolutionMap,
  baselineEmissions: EmissionsBySource,
  target: Partial<Target>,
  gap: number,
  carbonImpact: number,
  carbonImpactByScope: CarbonImpactByScope,
): Recommendation[] => {
  const result: Recommendation[] = [];

  const targetYearNumeric = Number(target?.targetYear);
  const targetGoalNumeric = target?.goal || 0;

  // Check for invalid input
  if (Object.values(solutionMap).length === 0 || !baselineEmissions) {
    return result;
  }

  // Efficiency recommendations
  const electricEfficiency = getSolutionValuePercent(
    solutionMap,
    SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryElectricity,
  );
  const gasEfficiency = getSolutionValuePercent(
    solutionMap,
    SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryNaturalGas,
  );

  if (electricEfficiency < ELECTRIC_EFFICIENCY_LOW_THRESHOLD) {
    result.push({
      ...recommendations[RecommendationId.ElectricEfficiencyLow],
      priority: calculatePriority(
        electricEfficiency,
        ELECTRIC_EFFICIENCY_TARGET,
      ),
    });
  }

  if (electricEfficiency > ELECTRIC_EFFICIENCY_HIGH_THRESHOLD) {
    result.push({
      ...recommendations[RecommendationId.ElectricEfficiencyHigh],
      priority: calculatePriority(
        electricEfficiency,
        ELECTRIC_EFFICIENCY_HIGH_THRESHOLD,
      ),
    });
  }

  if (gasEfficiency > NATURAL_GAS_EFFICIENCY_HIGH_THRESHOLD) {
    result.push({
      ...recommendations[RecommendationId.GasEfficiencyHigh],
      priority: calculatePriority(
        gasEfficiency,
        NATURAL_GAS_EFFICIENCY_HIGH_THRESHOLD,
      ),
    });
  }

  // Transportation fleet recommendations
  const hasGasElectrification =
    getSolutionValuePercent(
      solutionMap,
      SolutionOptionKey.MobileElectrificationGasoline,
    ) > 0;
  const hasDieselElectrification =
    getSolutionValuePercent(
      solutionMap,
      SolutionOptionKey.MobileElectrificationDiesel,
    ) > 0;

  if (baselineEmissions.gasoline > 0 && !hasGasElectrification) {
    result.push({
      ...recommendations[RecommendationId.GasolineFleet],
    });
  }

  if (baselineEmissions.diesel > 0 && !hasDieselElectrification) {
    result.push({
      ...recommendations[RecommendationId.DieselFleet],
    });
  }

  // Offsets recommendation
  const offsetsPercent = getSolutionValuePercent(
    solutionMap,
    SolutionOptionKey.OffsetsCarbon,
  );

  if (offsetsPercent > OFFSET_THRESHOLD) {
    result.push({
      ...recommendations[RecommendationId.HighOffsets],
    });
  }

  // Natural gas contribution recommendations
  if (
    gasEfficiency > NATURAL_GAS_EFFICIENCY_LOW_THRESHOLD &&
    !hasGasElectrification
  ) {
    result.push({
      ...recommendations[RecommendationId.GasEfficiencyElectrification],
      priority: calculatePriority(gasEfficiency, NATURAL_GAS_EFFICIENCY_TARGET),
    });
  }

  if (
    baselineEmissions.naturalGas > NATURAL_GAS_EMISSIONS_THRESHOLD &&
    gasEfficiency < NATURAL_GAS_EFFICIENCY_LOW_THRESHOLD
  ) {
    result.push({
      ...recommendations[RecommendationId.GasEfficiencyLow],
      priority: calculatePriority(gasEfficiency, ELECTRIC_EFFICIENCY_TARGET),
    });
  }

  const naturalGasElectrificationPercent =
    getSolutionValueDecimal(
      solutionMap,
      SolutionOptionKey.StationaryElectrificationNaturalGas,
    ) * 100;

  if (naturalGasElectrificationPercent > ELECTRIFICATION_HIGH_THRESHOLD) {
    result.push({
      ...recommendations[RecommendationId.HighElectrification],
      priority: calculatePriority(
        naturalGasElectrificationPercent,
        ELECTRIFICATION_HIGH_THRESHOLD,
      ),
    });
  }

  // solar is above 30%
  const solarPercent = getSolutionValuePercent(
    solutionMap,
    SolutionOptionKey.OnsiteSolarOrRenewableEnergyGenerationElectricity,
  );
  if (solarPercent > SOLAR_HIGH_THRESHOLD) {
    result.push({
      ...recommendations[RecommendationId.HighSolar],
      priority: calculatePriority(solarPercent, SOLAR_HIGH_THRESHOLD),
    });
  }

  // Target recommendations
  if (target && targetGoalNumeric < TARGET_MIN) {
    result.push({
      ...recommendations[RecommendationId.LowTarget],
      priority: calculatePriority(targetGoalNumeric, TARGET_MIN),
    });
  }

  if (targetYearNumeric < 2030) {
    result.push({
      ...recommendations[RecommendationId.EarlyTarget],
      priority: 0,
    });
  }

  // Emission to solution gap recommendations
  const baselineEmissionFuels: FuelSource[] = Object.entries(baselineEmissions)
    .filter(
      ([, emissionValue]) => emissionValue > EMISSION_CONTRIBUTION_THRESHOLD,
    )
    .map(([emissionSource]) => emissionSource as FuelSource);
  const targetedFuels: FuelSource[] = solutionOptions
    .filter((solutionOption) => {
      const selectedSolutionValue =
        solutionMap[solutionOption.name as SolutionOptionKey] || 0;
      return selectedSolutionValue > 0;
    })
    .map((solutionOption) => solutionOption.targetedFuelSource);
  const unaddressedFuelSources = difference(
    baselineEmissionFuels,
    targetedFuels,
  );
  const hasUnaddressedEmissions = unaddressedFuelSources.length > 0;

  if (hasUnaddressedEmissions) {
    result.push({
      ...recommendations[RecommendationId.UnaddressedEmissions],
    });
  }

  // Ineffective solutions recommendations
  const carbonCapturePercent = getSolutionValuePercent(
    solutionMap,
    SolutionOptionKey.CarbonCaptureAndSequestrationCarbon,
  );
  if (
    carbonCapturePercent > CARBON_CAPTURE_THRESHOLD &&
    targetYearNumeric < 2035
  ) {
    result.push({
      ...recommendations[RecommendationId.CarbonCaptureIneffective],
    });
  }

  const recPercent = getSolutionValuePercent(
    solutionMap,
    SolutionOptionKey.RenewableCreditsRecsElectricity,
  );
  if (
    recPercent > REC_THRESHOLD &&
    electricEfficiency < ELECTRIC_EFFICIENCY_LOW_THRESHOLD
  ) {
    result.push({
      ...recommendations[RecommendationId.HighRECs],
    });
  }

  const ppaPercent = getSolutionValuePercent(
    solutionMap,
    SolutionOptionKey.RenewableEnergyAsPpaVppaElectricity,
  );
  if (
    ppaPercent > PPA_THRESHOLD &&
    electricEfficiency < ELECTRIC_EFFICIENCY_LOW_THRESHOLD
  ) {
    result.push({
      ...recommendations[RecommendationId.HighPPA],
    });
  }

  // plan is less than target (negative gap is remaining work to achieve
  // target after accounting for plan)
  if (gap < 0) {
    result.push({
      ...recommendations[RecommendationId.PlanLessThanTarget],
      priority: calculatePriority(gap, 0),
    });
  }

  // plan is more than target + 5% (positive gap means overachievement)
  if (gap > GAP_THRESHOLD) {
    result.push({
      ...recommendations[RecommendationId.PlanGreaterThanTarget],
      priority: calculatePriority(gap, GAP_THRESHOLD),
    });
  }

  // Scope proportion recommendations
  const scope1Proportion = carbonImpactByScope[Scope.Scope1] / carbonImpact;
  if (scope1Proportion < SCOPE_PROPORTION_THRESHOLD) {
    result.push({
      ...recommendations[RecommendationId.Scope1Low],
      priority:
        calculatePriority(scope1Proportion, SCOPE_PROPORTION_THRESHOLD) * 100,
    });
  }

  const scope2Proportion = carbonImpactByScope[Scope.Scope2] / carbonImpact;
  if (scope2Proportion < SCOPE_PROPORTION_THRESHOLD) {
    result.push({
      ...recommendations[RecommendationId.Scope2Low],
      priority:
        calculatePriority(scope2Proportion, SCOPE_PROPORTION_THRESHOLD) * 100,
    });
  }

  return result.sort((a, b) => b.priority - a.priority);
};
